import React, { useState, useEffect, useCallback } from 'react';
import '../styles/components/image-slideshow.scss';

interface ImageSlideshowProps {
  images: {
    src: string;
    alt: string;
  }[];
  autoplaySpeed?: number;
}

const ImageSlideshow: React.FC<ImageSlideshowProps> = ({ 
  images, 
  autoplaySpeed = 5000 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  // Function to go to next slide
  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600); // Match this with the CSS transition duration
  }, [images.length, isTransitioning]);

  // Function to go to previous slide
  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600); // Match this with the CSS transition duration
  }, [images.length, isTransitioning]);

  // Function to jump to a specific slide
  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  // Preload adjacent images
  useEffect(() => {
    const imagesToLoad = [
      currentIndex,
      (currentIndex + 1) % images.length,
      (currentIndex - 1 + images.length) % images.length
    ];
    
    imagesToLoad.forEach(index => {
      if (!loadedImages.has(index) && !imageErrors.has(index)) {
        const img = new Image();
        img.onload = () => {
          setLoadedImages(prev => new Set(prev).add(index));
        };
        img.onerror = () => {
          console.error(`Failed to load image: ${images[index].src}`);
          setImageErrors(prev => new Set(prev).add(index));
        };
        img.src = images[index].src;
      }
    });
  }, [currentIndex, images, loadedImages, imageErrors]);

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, autoplaySpeed);
    
    return () => clearInterval(interval);
  }, [nextSlide, autoplaySpeed]);

  if (!images || images.length === 0) {
    return <div className="slideshow-container empty">No images to display</div>;
  }

  return (
    <div className="slideshow-container">
      <div className="slideshow-track">
        {images.map((image, index) => (
          <div 
            key={index} 
            className={`slideshow-slide ${index === currentIndex ? 'active' : ''}`}
            style={{ 
              transform: `translateX(${100 * (index - currentIndex)}%)`,
              opacity: index === currentIndex ? 1 : 0
            }}
          >
            {imageErrors.has(index) ? (
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#1a1a1a',
                color: '#666'
              }}>
                Image failed to load
              </div>
            ) : (
              <img 
                src={image.src} 
                alt={image.alt}
                loading={index === 0 ? "eager" : "lazy"}
                onError={() => {
                  console.error(`Image load error: ${image.src}`);
                  setImageErrors(prev => new Set(prev).add(index));
                }}
              />
            )}
          </div>
        ))}
      </div>
      
      <button className="slideshow-arrow prev" onClick={prevSlide}>
        <span>&#10094;</span>
      </button>
      <button className="slideshow-arrow next" onClick={nextSlide}>
        <span>&#10095;</span>
      </button>
      
      <div className="slideshow-dots">
        {images.map((_, index) => (
          <button 
            key={index} 
            className={`slideshow-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageSlideshow;